/**
 * 读卡器读取的证件详情
 */
import React, { useImperativeHandle, useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import intl from 'react-intl-universal';

import { FormItem } from '@/components/Reservation/ContentBlock';
import DateJS from '@/utils/date';
import { CheckInGuestInfo } from '@/redux/reducers/reservation/base';
import { SEX_VALUE_SYMBOL } from '@/config/constant';
import { isStandingCardTime } from '@/utils/transform';
import { DATE_FORMAT } from '@/config';

import styles from './index.module.scss';

export type IDCardDetailInfo = Pick<
  CheckInGuestInfo,
  | 'name'
  | 'sex'
  | 'nation'
  | 'birthday'
  | 'certificateLocation'
  | 'certNo'
  | 'issuingAuthority'
  | 'certificateStartTime'
  | 'certificateEndTime'
>;

export interface DetailModalRef {
  openModal: (value: IDCardDetailInfo) => void;
}

interface IProps {
  myRef?: React.Ref<DetailModalRef>;
}

const SEX_NAME_MAP = {
  [SEX_VALUE_SYMBOL.Male]: '男',
  [SEX_VALUE_SYMBOL.Female]: '女',
};

const Index: React.FC<IProps> = ({ myRef }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<IDCardDetailInfo>({} as IDCardDetailInfo);

  // 关闭详情
  const closeModal = () => {
    setVisible(false);
  };

  // 格式化内容
  const formatContent = useMemo(() => {
    const { birthday, certificateStartTime, certificateEndTime } = detailData;
    const birthDayDate = DateJS(birthday);
    const validStartDate = DateJS(certificateStartTime);
    const validEndDate = DateJS(certificateEndTime);
    const birthDayDateStr = birthDayDate.isValid() ? birthDayDate.format(DATE_FORMAT) : birthday; // 生日
    const validStartDateStr = validStartDate.isValid()
      ? validStartDate.format(DATE_FORMAT)
      : certificateStartTime; // 有效期限开始
    const formatEndDate = validEndDate.isValid()
      ? validEndDate.format(DATE_FORMAT)
      : certificateEndTime;
    const validEndDateStr = isStandingCardTime(certificateEndTime) ? '长期' : formatEndDate; // 有效期限结束
    return {
      birthDayDateStr,
      validRangeDateStr: `${validStartDateStr}-${validEndDateStr}`,
    };
  }, [detailData]);

  // ref引用
  useImperativeHandle(
    myRef,
    () => ({
      openModal: (data) => {
        setVisible(true);
        setDetailData(data);
      },
    }),
    [],
  );

  return (
    <Modal
      wrapClassName={styles.detailModalWrapper}
      title={intl.get('drawer_checkin_guest_detail_title')}
      destroyOnClose
      centered
      maskClosable={false}
      open={visible}
      onCancel={closeModal}
      footer={
        <Button type="primary" onClick={closeModal}>
          {intl.get('app_confirm_verify_btn')}
        </Button>
      }
    >
      <div className={styles.detailContent}>
        <FormItem label={intl.get('drawer_checkin_guest_detail_name_label')}>
          {detailData.name}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_gender_label')}>
          {SEX_NAME_MAP[detailData.sex] || intl.get('drawer_checkin_guest_detail_gender_unknown')}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_nation_label')}>
          {detailData.nation}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_birthday_label')}>
          {formatContent.birthDayDateStr}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_location_label')}>
          {detailData.certificateLocation}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_cert_label')}>
          {detailData.certNo}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_author_org_label')}>
          {detailData.issuingAuthority}
        </FormItem>
        <FormItem label={intl.get('drawer_checkin_guest_detail_valid_time_label')}>
          {formatContent.validRangeDateStr}
        </FormItem>
      </div>
    </Modal>
  );
};

export default React.forwardRef<DetailModalRef>((props, ref) => <Index {...props} myRef={ref} />);
